import { shownId } from './partsCatalogue';
import type { ByPart } from './partModelSize';
import type { PartItem } from './partsRun';
import { touchRows, type HardwareAlone, type HardwareInteraction } from './hardwareInteractionsRun';
import { hardwareLine, nameOf } from './stepHolesPrompt';
import { FRONT_VIEW_WORDS, TOP_VIEW_WORDS, VIEW_BOX_WORDS, VIEW_TILT_WORDS } from './partViews';

// THE INSERTION TABLE'S QUESTION: for each piece of hardware (covers are not asked), along which axis of its 3D
// model it goes into its hole, how wide that hole is, and how much of it ends up inside the parts
// and outside them. Every part as the parts list prints it, its 3D model's three dimensions, the
// holes the joints table puts it in — and two pictures of each model, the ones Phase 7's movement
// call reads a part's axis off (see partViews).

export type InsertionContext = {
  items: PartItem[];
  // The hardware asked about, by stored id.
  ids: string[];
  // What each part's 3D model measures (see partModelSize).
  sizes: ByPart;
  table: { interactions: HardwareInteraction[]; alone: HardwareAlone[] };
  // THE PARTS PICTURED, in the order their pictures are sent — two each, from the front and from
  // above (see insertionShots); each asked part followed by the washers on it. Absent or empty: no pictures, and no section about them.
  views?: string[];
};

const r1 = (v: number) => Math.round(v * 10) / 10;

const modelLine = (sizes: ByPart, id: string) => {
  const m = sizes[id];
  return m
    ? `3D model: width (x) ${r1(m.x)} mm, depth (y) ${r1(m.y)} mm, height (z) ${r1(m.z)} mm`
    : '3D model: none';
};

// AND THE WASHERS IT GOES IN WITH, as the joints table puts them on it (HardwareInteraction.washers):
// each with its printed size and its model's — a washer is not asked about, it changes how far the
// piece it is on goes in.
export const washersOn = (table: InsertionContext['table'], id: string): string[] =>
  [...new Set(table.interactions.flatMap((one) =>
    Object.entries(one.washers).filter(([, on]) => on === id).map(([w]) => w)))];

const partBlock = ({ items, sizes, table }: InsertionContext, id: string): string => {
  const washers = washersOn(table, id);
  return [
    hardwareLine(items, id),
    `    ${modelLine(sizes, id)}`,
    ...washers.map((w) => `    goes in with the washer ${hardwareLine(items, w).trim()}; ${modelLine(sizes, w)}`),
  ].join('\n');
};

// Which stretch of the hardware is in a hole, as the joints table says it (TableHole.portion).
const PORTION = { leading: 'leading end', trailing: 'trailing end', middle: 'middle', whole: 'all of it' };

// THE HOLES EACH PIECE GOES INTO, as the joints table says them — one line per piece, each kind of
// hole once: a joint made on the left and on the right is the same holes twice.
const holesBlock = ({ items, ids, table }: InsertionContext): string => {
  const rows = touchRows(table);
  return ids.map((id) => {
    const holes = [...new Set(rows.flatMap((row) => row.holes)
      .filter((h) => h.for === id && h.kind !== 'none')
      .map((h) => `${h.kind} in ${nameOf(items, h.part)}${h.portion ? ` (${PORTION[h.portion]})` : ''}`))];
    return `- ${shownId(id)}: ${holes.length ? holes.join('; ') : 'no hole'}`;
  }).join('\n');
};

// WHAT THE PICTURES ARE: two per part, said in the words every call sending them uses (partViews).
const viewsBlock = ({ items, views }: InsertionContext): string[] => (views?.length ? [
  '',
  'PICTURES: two of each part\'s 3D model, in this order:',
  views.map((id, i) => `  ${2 * i + 1}. ${shownId(id)}: ${nameOf(items, id)} — ${FRONT_VIEW_WORDS}\n`
    + `  ${2 * i + 2}. ${shownId(id)}: ${nameOf(items, id)} — ${TOP_VIEW_WORDS}`).join('\n'),
  VIEW_TILT_WORDS,
  VIEW_BOX_WORDS,
  'Read each part\'s axis off its pictures, not off its name.',
] : []);

export function buildInsertionPrompt(ctx: InsertionContext): string {
  return [
    'These are the hardware parts of a piece of flat-pack furniture. For each one, say how '
    + 'it goes into the hole it fills.',
    '',
    'PARTS (id: name — printed size; then its 3D model\'s dimensions, along the model\'s own axes):',
    ctx.ids.map((id) => partBlock(ctx, id)).join('\n'),
    '',
    'THE HOLES EACH GOES INTO (kind of hole, the part, which stretch of it is in that hole):',
    holesBlock(ctx),
    ...viewsBlock(ctx),
    '',
    'For each part, answer:',
    '- axis: the axis of ITS 3D MODEL ("x", "y" or "z") it is pushed or driven into its hole along.',
    '- holeDiameterMm: the diameter of the hole it goes into, in mm.',
    '- insideMm: once assembled, how much of its length along that axis is inside any part — every '
    + 'part it goes into together, however many — in mm. insideMm + outsideMm is its whole length.',
    '- outsideMm: how much of that length is left outside every part, in mm; 0 where all of it is inside. '
    + 'Where it goes in with a washer, that washer\'s thickness is outside too, on top of its head: '
    + 'the washer sits between the head and the part.',
    '',
    'Reply with JSON only: {"parts": [{"id": <id>, "axis": "x" | "y" | "z", "holeDiameterMm": <mm> | '
    + 'null, "insideMm": <mm>, "outsideMm": <mm>}]}, one entry per part above.',
  ].join('\n');
}
